import { print } from "graphql";
import { Client } from "graphql-ws";
import localforage from "localforage";
import { syncLocationById } from "./use-get-location-by-id";
import { getOperationalOrdersQueryKey } from "./use-get-operational-orders";
import { getOrderByIdQueryKey } from "./use-get-order-by-id";
import { addNewNotification } from "./use-subscribe-to-venue-notifications";
import { getOperationalLocationsQueryKey } from "./use-get-operational-locations";
import { getPrepStationsOrdersQueryKey } from "~/queries/use-get-prep-stations-orders";
import { STORAGE_KEYS } from "~/libs/storage-keys";
import { Notification } from "~/types/store";
import {
	isDigitalMenuOrder,
	shouldNotifyForDigitalMenuOrder,
} from "~/utils/digital-menu-notification-locations";
import {
	GetOperationalOrdersQuery,
	SubscribeToVenueOrdersDocument,
	SubscribeToVenueOrdersSubscription,
} from "~/types/__generated/gql/graphql";
import { queryClient } from "~/providers/query-provider";

void (
	/* GraphQL */ `
		subscription SubscribeToVenueOrders {
			subscribeToVenueOrders {
				type
				id
				payload {
					_id
					created_at
					updated_at
					index
					status
					source
					location
				}
			}
		}
	`
);

export const subscribeToVenueOrders = async (client: Client) => {
	const subscription = client.iterate<SubscribeToVenueOrdersSubscription>({
		query: print(SubscribeToVenueOrdersDocument),
	});
	for await (const result of subscription) {
		if (result.data) {
			const { type, payload } = result.data.subscribeToVenueOrders;
			switch (type) {
				case "create": {
					const selectedLocationIds = payload
						? await localforage.getItem<string[] | null>(
								STORAGE_KEYS.DIGITAL_MENU_NOTIFICATION_LOCATION_IDS,
							)
						: null;
					if (payload) {
						void queryClient.invalidateQueries(
							getOrderByIdQueryKey(payload._id),
							{ exact: true },
						);
						void syncLocationById(payload.location, {
							orderId: payload._id,
							updatedAt: payload.updated_at,
						});
						void queryClient.invalidateQueries({
							queryKey: getOperationalOrdersQueryKey(),
							exact: true,
						});
						void queryClient.invalidateQueries({
							queryKey: getPrepStationsOrdersQueryKey(),
							exact: true,
						});
						void queryClient.invalidateQueries({
							queryKey: getOperationalLocationsQueryKey(),
							exact: true,
						});
					}
					if (
						payload?.status === "placed" &&
						(!isDigitalMenuOrder(payload) ||
							shouldNotifyForDigitalMenuOrder(
								payload,
								selectedLocationIds ?? null,
							))
					) {
						const newNotification: Notification = {
							id: payload._id,
							message: `New order #${payload.index}`,
							type: "NEW_ORDER",
							severity: "warning",
							customProps: {
								status: payload.status,
								locationId: payload.location,
								source: payload.source,
							},
						};
						addNewNotification(newNotification);
					}
					break;
				}
				case "update": {
					if (payload) {
						void queryClient.invalidateQueries(
							getOrderByIdQueryKey(payload._id),
							{ exact: true },
						);
						void syncLocationById(payload.location, {
							orderId: payload._id,
							updatedAt: payload.updated_at,
						});
						void queryClient.invalidateQueries({
							queryKey: getOperationalOrdersQueryKey(),
							exact: true,
						});
						void queryClient.invalidateQueries({
							queryKey: getPrepStationsOrdersQueryKey(),
							exact: true,
						});
						void queryClient.invalidateQueries({
							queryKey: getOperationalLocationsQueryKey(),
							exact: true,
						});
					}
					break;
				}
			}
		}
	}
};

export const regroupOrders = (
	newOrder: ArrayItem<GqlResultOf<GetOperationalOrdersQuery>>,
	oldGroups: GqlResultOf<GetOperationalOrdersQuery>,
) => {
	const isExisting = oldGroups.some((o) => o._id === newOrder._id);
	if (isExisting) {
		return oldGroups.map((o) => (o._id === newOrder._id ? newOrder : o));
	}
	return oldGroups.concat(newOrder);
};
